import React, { useState, useEffect, useCallback } from 'react';
import {
  Input,
  Spinner,
  makeStyles,
  tokens,
  TagGroup,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  Avatar,
  Text,
  Badge,
  RadioGroup,
  Radio,
  Tooltip
} from '@fluentui/react-components';
import { 
  SearchRegular, 
  PeopleTeamRegular, 
  PersonRegular,
  DismissRegular 
} from '@fluentui/react-icons';
import { robustGroupService } from '../services/robustGroupService';

// Simple debounce function
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export interface SelectedEntity {
  id: string;
  displayName: string;
  type: 'user' | 'group';
  subType?: string;
  description?: string;
  userPrincipalName?: string; // User email for user assignments
  mail?: string; // Fallback email field for user assignments
}

interface UserGroupSelectorWithRadioProps {
  value?: SelectedEntity[];
  onChange?: (selectedEntities: SelectedEntity[]) => void;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  required?: boolean;
  showIndividualUsersOption: boolean;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    width: '100%'
  },
  radioGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalS
  },
  searchContainer: {
    position: 'relative',
    width: '100%'
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  input: {
    width: '100%'
  },
  loadingIndicator: {
    position: 'absolute',
    right: tokens.spacingHorizontalM,
    pointerEvents: 'none'
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    zIndex: 1000,
    maxHeight: '300px',
    overflowY: 'auto',
    marginTop: tokens.spacingVerticalXS
  },
  dropdownItem: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    textAlign: 'left',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover
    },
    '&:focus': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      outline: `2px solid ${tokens.colorBrandForeground1}`,
      outlineOffset: '-2px'
    },
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  entityInfo: {
    flex: 1,
    minWidth: 0
  },
  entityName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  entityDescription: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  entityType: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic'
  },
  noResults: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200
  },
  errorMessage: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200
  },
  selectedContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS
  },
  helpText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXXS
  }
});

export const UserGroupSelectorWithRadio: React.FC<UserGroupSelectorWithRadioProps> = ({
  value = [],
  onChange,
  placeholder,
  disabled = false,
  multiple = true,
  required = false,
  showIndividualUsersOption,
}) => {
  const styles = useStyles();
  const [searchType, setSearchType] = useState<'group' | 'user'>('group');
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<SelectedEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<SelectedEntity[]>(value);

  // Sync internal state with prop value when it changes
  useEffect(() => {
    setSelectedEntities(value);
  }, [value]);

  // Update placeholder based on search type
  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    if(!showIndividualUsersOption) return 'Search for security groups...';
    return searchType === 'user' 
      ? 'Search for users by name or email...' 
      : 'Search for security groups...';
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query: string, type: 'user' | 'group') => {
      if (query.trim().length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Use the new searchEntities method that properly filters by type
        const result = await robustGroupService.searchEntities(query, type);

        // Filter out already selected entities and ensure correct type
        // Also filter out groups that are already in use (isInUse: true)
        const filteredResults: SelectedEntity[] = result.entities
          .filter((entity: any) => {
            // For groups, exclude those that are already in use
            if (entity.type === 'group' && entity.isInUse === true) {
              return false;
            }
            return true;
          })
          .map((entity: any) => ({
            id: entity.id,
            displayName: entity.displayName,
            type: entity.type as 'user' | 'group',
            subType: entity.subType,
            description: entity.description || (entity.type === 'user' ? entity.userPrincipalName || entity.mail : entity.description)
          }))
          .filter(
            (entity: SelectedEntity) => !selectedEntities.some(selected => selected.id === entity.id)
          );

        setSearchResults(filteredResults);
        setShowDropdown(filteredResults.length > 0 || query.trim().length >= 2);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
        setSearchResults([]);
        setShowDropdown(true);
      } finally {
        setLoading(false);
      }
    }, 300),
    [selectedEntities]
  );

  useEffect(() => {
    debouncedSearch(inputValue, searchType);
  }, [inputValue, searchType, debouncedSearch]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleEntitySelect = (entity: SelectedEntity) => {
    let newSelection: SelectedEntity[];

    if (multiple) {
      // Prevent duplicate selections
      if (selectedEntities.some(e => e.id === entity.id)) {
        console.warn('Entity already selected:', entity.displayName);
        return;
      }
      newSelection = [...selectedEntities, entity];
    } else {
      newSelection = [entity];
    }

    console.log('✅ Entity selected:', entity.displayName, 'Type:', entity.type);
    setSelectedEntities(newSelection);
    onChange?.(newSelection);
    setInputValue('');
    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleEntityRemove = (entityId: string) => {
    const entityToRemove = selectedEntities.find(e => e.id === entityId);
    if (entityToRemove) {
      console.log('❌ Entity removed:', entityToRemove.displayName, 'Type:', entityToRemove.type);
    }

    const newSelection = selectedEntities.filter(e => e.id !== entityId);
    setSelectedEntities(newSelection);
    onChange?.(newSelection);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => setShowDropdown(false), 200);
  };

  const handleInputFocus = () => {
    if (inputValue.trim().length >= 2 && searchResults.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleSearchTypeChange = (ev: any, data: any) => {
    setSearchType(data.value);
    setInputValue('');
    setSearchResults([]);
    setShowDropdown(false);
  };

  return (
    <div className={styles.container} data-testid="user-group-selector-radio">
      {/* Radio button selection */}
      {showIndividualUsersOption && <RadioGroup 
        className={styles.radioGroup}
        value={searchType}
        onChange={handleSearchTypeChange}
        layout="horizontal"
        disabled={disabled}
        data-testid="user-group-selector-type-toggle"
      >
        <Radio value="group" label="Security Group" data-testid="user-group-selector-radio-option-group" />
        <Radio value="user" label="Individual Users" data-testid="user-group-selector-radio-option-user" />
      </RadioGroup>}

      {/* Selected entities */}
      {selectedEntities.length > 0 && (
        <TagGroup aria-label="Selected users and groups" data-testid="user-group-selector-radio-selected">
          {selectedEntities.map((entity, index) => (
            <InteractionTag
              key={entity.id}
              size="medium"
              appearance="brand"
              data-testid={`user-group-selector-radio-selected-tag-${index}`}
            >
              <Tooltip content={entity.displayName} relationship="label" data-testid={`user-group-selector-radio-selected-tooltip-${index}`}>
                <InteractionTagPrimary
                  media={entity.type === 'user' ? <PersonRegular /> : <PeopleTeamRegular />}
                  style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  data-testid={`user-group-selector-radio-selected-primary-${index}`}
                >
                  {/* Show full name for users, not truncated */}
                  {entity.displayName}
                  {entity.type === 'user' && (
                    <Badge size="extra-small" appearance="tint" style={{ marginLeft: '4px' }} data-testid={`user-group-selector-radio-selected-user-badge-${index}`}>User</Badge>
                  )}
                </InteractionTagPrimary>
              </Tooltip>
              {!disabled && (
                <InteractionTagSecondary
                  aria-label={`Remove ${entity.displayName}`}
                  onClick={() => handleEntityRemove(entity.id)}
                  data-testid={`user-group-selector-radio-remove-${index}`}
                >
                  <DismissRegular />
                </InteractionTagSecondary>
              )}
            </InteractionTag>
          ))}
        </TagGroup>
      )}

      {/* Search input */}
      <div className={styles.searchContainer} data-testid="user-group-selector-radio-search-container">
        <div className={styles.inputContainer} data-testid="user-group-selector-radio-input-container">
          <Input
            className={styles.input}
            value={inputValue}
            onChange={(_, data) => handleInputChange(data.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={getPlaceholder()}
            disabled={disabled}
            contentBefore={<SearchRegular />}
            required={required && selectedEntities.length === 0}
            data-testid="user-group-selector-radio-input"
          />
          {loading && (
            <div className={styles.loadingIndicator} data-testid="user-group-selector-radio-loading-indicator">
              <Spinner size="tiny" data-testid="user-group-selector-radio-loading-spinner" />
            </div>
          )}
        </div>

        {/* Dropdown results */}
        {showDropdown && (
          <div className={styles.dropdown} data-testid="user-group-selector-radio-dropdown">
            {error ? (
              <div className={styles.errorMessage} data-testid="user-group-selector-radio-error-message">
                {error}
              </div>
            ) : searchResults.length > 0 ? (
              searchResults.map((entity) => (
                <button
                  key={entity.id}
                  className={styles.dropdownItem}
                  onClick={() => handleEntitySelect(entity)}
                  type="button"
                  aria-label={`Select ${entity.type === 'user' ? 'user' : 'group'} ${entity.displayName}`} data-testid="user-group-selector-radio-option"
                >
                  <Avatar
                    name={entity.displayName}
                    size={24}
                    icon={entity.type === 'user' ? <PersonRegular /> : <PeopleTeamRegular />}
                    color={entity.type === 'user' ? 'colorful' : 'brand'}
                    data-testid="user-group-selector-radio-avatar"
                  />
                  <div className={styles.entityInfo} data-testid="user-group-selector-radio-entity-info">
                    <div className={styles.entityName} data-testid="user-group-selector-radio-entity-name">
                      {entity.displayName}
                    </div>
                    {entity.description && (
                      <div className={styles.entityDescription} data-testid="user-group-selector-radio-entity-description">
                        {entity.description}
                      </div>
                    )}
                    {entity.subType && (
                      <div className={styles.entityType} data-testid="user-group-selector-radio-entity-type">
                        {entity.type === 'user' ? entity.subType : `${entity.subType} Group`}
                      </div>
                    )}
                  </div>
                </button>
              ))
            ) : inputValue.trim().length >= 2 ? (
              <div className={styles.noResults} data-testid="user-group-selector-radio-no-results">
                No {searchType === 'user' ? 'users' : 'groups'} found for "{inputValue}"
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Help text */}
      {!disabled && (
        <Text className={styles.helpText} data-testid="user-group-selector-radio-help-text">
          {searchType === 'user' 
            ? multiple 
              ? 'You can select multiple individual users. Type at least 2 characters to search.'
              : 'Select an individual user. Type at least 2 characters to search.'
            : multiple
              ? 'You can select multiple security groups. Type at least 2 characters to search.'
              : 'Select a security group. Type at least 2 characters to search.'}
        </Text>
      )}
    </div>
  );
};


export default UserGroupSelectorWithRadio;